import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  EventBusy as EventBusyIcon,
  BeachAccess as BeachAccessIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  Computer as ComputerIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  formatReportLastVisited,
  getRecentReportPaths,
  getReportFavorites,
  getReportUsageEntry,
  isReportFavorite,
  recordReportVisit,
  toggleReportFavorite,
} from '../utils/reportPreferences';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ReportCardDefinition {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  roles: string[];
  color: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const REPORT_CARDS: ReportCardDefinition[] = [
  {
    title: 'Time Records Report',
    description: 'View detailed time entries with filtering and role-based access control',
    icon: <HistoryIcon sx={{ fontSize: 40 }} />,
    path: '/reports/time-records',
    roles: ['agent', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'primary',
  },
  {
    title: 'Daily Summary Report',
    description: 'Daily overview of employee attendance and work hours',
    icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
    path: '/reports/daily-summary',
    roles: ['agent', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'secondary',
  },
  {
    title: 'Payroll Review',
    description: 'Pay-period payable hours, exceptions, approval, lock, and payroll export from the canonical seconds engine',
    icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
    path: '/reports/payroll-review',
    roles: ['hr', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'success',
  },
  {
    title: 'Campaign Performance',
    description: 'Performance metrics and analytics by campaign',
    icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
    path: '/reports/campaign-performance',
    roles: ['manager', 'administrator', 'super_user'],
    color: 'success',
  },
  {
    title: 'Employee Attendance',
    description: 'Comprehensive employee attendance tracking and patterns',
    icon: <PeopleIcon sx={{ fontSize: 40 }} />,
    path: '/reports/employee-attendance',
    roles: ['agent', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'info',
  },
  {
    title: 'Employee Time Sheet',
    description: 'Summary of worked hours, absences, tardies, and early departures',
    icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
    path: '/reports/employee-time-sheet',
    roles: ['agent', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'secondary',
  },
  {
    title: 'Weekly Attendance Report',
    description: 'Detailed weekly timesheets grouped by employee and week (2 weeks per page)',
    icon: <CalendarIcon sx={{ fontSize: 40 }} />,
    path: '/reports/weekly-attendance',
    roles: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'primary',
  },
  {
    title: 'Weekly Staffing & Coverage Report',
    description: 'Official workforce activity report for customer billing — schedules, attendance, coverage, and notifications',
    icon: <CalendarIcon sx={{ fontSize: 40 }} />,
    path: '/reports/weekly-staffing-coverage',
    roles: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'success',
  },
  {
    title: 'Time Off Requests Report',
    description: 'View employee time off requests with status, type, and requested dates',
    icon: <BeachAccessIcon sx={{ fontSize: 40 }} />,
    path: '/reports/time-off-requests',
    roles: ['supervisor', 'manager', 'administrator', 'super_user'],
    color: 'secondary',
  },
  {
    title: 'Absence Report',
    description: 'Track and manage employee absences, sick leave, and vacation requests',
    icon: <EventBusyIcon sx={{ fontSize: 40 }} />,
    path: '/reports/absence',
    roles: ['manager', 'administrator', 'super_user'],
    color: 'warning',
  },
  {
    title: 'Tardies Report',
    description: 'Track employee late arrivals and punctuality with scheduled vs actual clock-in times',
    icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
    path: '/reports/tardies',
    roles: ['manager', 'administrator', 'super_user'],
    color: 'error',
  },
  {
    title: 'Time Entry Validation',
    description: 'Identify missing punches and out-of-sequence time entries',
    icon: <WarningIcon sx={{ fontSize: 40 }} />,
    path: '/reports/time-entry-validation',
    roles: ['administrator', 'super_user'],
    color: 'warning',
  },
  {
    title: 'Manual Time Record Adjustments',
    description: 'HR/Payroll review of manual time adds, edits, and deletes with attendance impact',
    icon: <HistoryIcon sx={{ fontSize: 40 }} />,
    path: '/reports/manual-time-adjustments',
    roles: ['hr', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'warning',
  },
  {
    title: 'Scheduled Employees Report',
    description: 'Track employees who are scheduled to work and their attendance status',
    icon: <ScheduleIcon sx={{ fontSize: 40 }} />,
    path: '/reports/scheduled-employees',
    roles: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'info',
  },
  {
    title: 'Missing Punches Report',
    description: 'Identify employees missing clock outs, break ends, or lunch ends with notes, corrections, and audit trail',
    icon: <AssessmentIcon sx={{ fontSize: 40 }} />,
    path: '/reports/missing-punches',
    roles: ['team_lead', 'supervisor', 'manager', 'administrator', 'super_user'],
    color: 'error',
  },
  {
    title: 'Idle Summary Report',
    description: 'Track employee activity and idle time with detailed breakdowns by device and date',
    icon: <ComputerIcon sx={{ fontSize: 40 }} />,
    path: '/reports/idle-summary',
    roles: ['administrator', 'super_user'],
    color: 'info',
  },
  {
    title: 'Device Uptime Report',
    description: 'Monitor device connectivity, agent status, and last heartbeat timestamps',
    icon: <ComputerIcon sx={{ fontSize: 40 }} />,
    path: '/reports/device-uptime',
    roles: ['administrator', 'super_user'],
    color: 'primary',
  },
];

const ReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [prefsVersion, setPrefsVersion] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();

  const availableReports = useMemo(
    () => REPORT_CARDS.filter((report) => report.roles.includes(user?.role || '')),
    [user?.role],
  );

  const reportsByPath = useMemo(
    () => new Map(availableReports.map((report) => [report.path, report])),
    [availableReports],
  );

  const favoriteReports = useMemo(() => {
    const paths = getReportFavorites(user?.id).filter((path) => reportsByPath.has(path));
    return paths.map((path) => reportsByPath.get(path)!);
  }, [user?.id, reportsByPath, prefsVersion]);

  const recentReports = useMemo(() => {
    const paths = getRecentReportPaths(user?.id).filter((path) => reportsByPath.has(path));
    return paths.map((path) => reportsByPath.get(path)!);
  }, [user?.id, reportsByPath, prefsVersion]);

  const handleOpenReport = (path: string) => {
    recordReportVisit(user?.id, path);
    setPrefsVersion((value) => value + 1);
    navigate(path);
  };

  const handleToggleFavorite = (path: string) => {
    toggleReportFavorite(user?.id, path);
    setPrefsVersion((value) => value + 1);
  };

  const renderReportCard = (report: ReportCardDefinition, meta?: { lastVisited?: string; visitCount?: number }) => (
    <Grid item xs={12} sm={6} md={4} key={report.path}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
            <Box sx={{ color: `${report.color}.main`, mr: 1, flexShrink: 0 }}>
              {report.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" component="h2" sx={{ lineHeight: 1.25 }}>
                {report.title}
              </Typography>
              {meta?.lastVisited && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Last opened {formatReportLastVisited(meta.lastVisited)}
                  {meta.visitCount && meta.visitCount > 1 ? ` · ${meta.visitCount} visits` : ''}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              aria-label={isReportFavorite(user?.id, report.path) ? 'Remove favorite' : 'Add favorite'}
              onClick={() => handleToggleFavorite(report.path)}
              sx={{ color: isReportFavorite(user?.id, report.path) ? 'warning.main' : 'action.disabled' }}
            >
              {isReportFavorite(user?.id, report.path) ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
            {report.description}
          </Typography>

          <Button
            variant="contained"
            color={report.color as 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error'}
            fullWidth
            onClick={() => handleOpenReport(report.path)}
            sx={{ mt: 'auto' }}
          >
            View Report
          </Button>
        </CardContent>
      </Card>
    </Grid>
  );

  const renderReportGrid = (reports: ReportCardDefinition[], emptyMessage: string) => (
    reports.length > 0 ? (
      <Grid container spacing={3}>
        {reports.map((report) => {
          const usage = getReportUsageEntry(user?.id, report.path);
          return renderReportCard(report, usage ? {
            lastVisited: usage.lastVisitedAt,
            visitCount: usage.count,
          } : undefined);
        })}
      </Grid>
    ) : (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body1" color="text.secondary">{emptyMessage}</Typography>
      </Box>
    )
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Reports & Analytics
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Access comprehensive reports and analytics based on your role permissions.
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} aria-label="reports tabs">
          <Tab label="Available Reports" />
          <Tab label={`Favorites${favoriteReports.length ? ` (${favoriteReports.length})` : ''}`} />
          <Tab label="Quick Stats" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        {renderReportGrid(
          availableReports,
          'No reports available for your role. Contact your administrator to request access.',
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        {renderReportGrid(
          favoriteReports,
          'Star reports from Available Reports to pin them here.',
        )}
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Access
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Recently used reports
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {recentReports.slice(0, 4).map((report) => (
                    <Button
                      key={report.path}
                      variant="outlined"
                      startIcon={report.icon}
                      onClick={() => handleOpenReport(report.path)}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      {report.title}
                    </Button>
                  ))}
                  {recentReports.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Open reports to build your quick access list.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Role Permissions
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Your current role: <strong>{user?.role}</strong>
                </Typography>
                <Typography variant="body2">
                  You have access to {availableReports.length} report{availableReports.length !== 1 ? 's' : ''} based on your role permissions.
                </Typography>
                {favoriteReports.length > 0 && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {favoriteReports.length} favorite{favoriteReports.length !== 1 ? 's' : ''} saved.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
};

export default ReportsPage;
